import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  getCountFromServer,
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { Automation, AutomationExecution, automationService, AutomationStep, AutomationAction } from './automationService';
import { contactService } from './contactService';

export interface SystemAutomationStats {
  totalAutomations: number;
  activeAutomations: number;
  pausedAutomations: number;
  draftAutomations: number;
  archivedAutomations: number;
  
  totalExecutions: number;
  runningExecutions: number;
  completedExecutions: number;
  failedExecutions: number;
  
  totalEmailsSent: number;
  totalSmsSent: number;
  totalPushSent: number;
  
  averageCompletionRate: number;
  averageExecutionTime: number;
  
  topPerformingAutomations: { id: string; name: string; successRate: number }[];
  recentFailures: { id: string; error: string; timestamp: Date }[];
}

export interface AutomationWithUser extends Automation {
  userEmail?: string;
  userName?: string;
}

export interface ExecutionWithDetails extends AutomationExecution {
  automationName?: string;
  userEmail?: string;
  contactEmail?: string;
}

class AdminAutomationService {
  // Get all automations across all users
  async getAllAutomations(filters?: {
    status?: string;
    userId?: string;
    searchTerm?: string;
    category?: string | string[];
    limit?: number;
  }): Promise<AutomationWithUser[]> {
    try {
      let q = query(collection(db, 'automations'));
      
      // Apply category filter
      if (filters?.category) {
        if (Array.isArray(filters.category)) {
          q = query(q, where('category', 'in', filters.category));
        } else {
          q = query(q, where('category', '==', filters.category));
        }
      }
      
      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        q = query(q, where('status', '==', filters.status));
      }
      
      if (filters?.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }
      
      // Order by most recent
      q = query(q, orderBy('updatedAt', 'desc'));
      
      // Apply limit
      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }
      
      const snapshot = await getDocs(q);
      const automations: AutomationWithUser[] = [];
      
      // Get user details for each automation
      for (const docSnap of snapshot.docs) {
        const automation = { id: docSnap.id, ...docSnap.data() } as AutomationWithUser;
        
        // Fetch user details
        try {
          const userDoc = await getDoc(doc(db, 'users', automation.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            automation.userEmail = userData.email;
            automation.userName = userData.displayName || userData.firstName + ' ' + userData.lastName;
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
        
        // Apply search filter if provided
        if (filters?.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matches = 
            automation.name.toLowerCase().includes(searchLower) ||
            automation.description?.toLowerCase().includes(searchLower) ||
            automation.userEmail?.toLowerCase().includes(searchLower) ||
            automation.userName?.toLowerCase().includes(searchLower);
          
          if (matches) {
            automations.push(automation);
          }
        } else {
          automations.push(automation);
        }
      }
      
      return automations;
    } catch (error) {
      console.error('Error getting all automations:', error);
      throw new Error('Failed to fetch automations');
    }
  }
  
  // Get all automation executions
  async getAllExecutions(filters?: {
    status?: string;
    automationId?: string;
    limit?: number;
  }): Promise<ExecutionWithDetails[]> {
    try {
      let q = query(collection(db, 'automationExecutions'));
      
      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        q = query(q, where('status', '==', filters.status));
      }
      
      if (filters?.automationId) {
        q = query(q, where('automationId', '==', filters.automationId));
      }
      
      // Order by most recent
      q = query(q, orderBy('startedAt', 'desc'));
      
      // Apply limit
      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }
      
      const snapshot = await getDocs(q);
      const executions: ExecutionWithDetails[] = [];
      
      // Get details for each execution
      for (const docSnap of snapshot.docs) {
        const execution = { id: docSnap.id, ...docSnap.data() } as ExecutionWithDetails;
        
        // Fetch automation details
        try {
          const automationDoc = await getDoc(doc(db, 'automations', execution.automationId));
          if (automationDoc.exists()) {
            const automationData = automationDoc.data();
            execution.automationName = automationData.name;
            
            // Fetch user details
            const userDoc = await getDoc(doc(db, 'users', automationData.userId));
            if (userDoc.exists()) {
              execution.userEmail = userDoc.data().email;
            }
          }
        } catch (error) {
          console.error('Error fetching execution details:', error);
        }
        
        // Fetch contact details
        try {
          const contactDoc = await getDoc(doc(db, 'contacts', execution.contactId));
          if (contactDoc.exists()) {
            execution.contactEmail = contactDoc.data().email;
          }
        } catch (error) {
          console.error('Error fetching contact details:', error);
        }
        
        executions.push(execution);
      }
      
      return executions;
    } catch (error) {
      console.error('Error getting all executions:', error);
      throw new Error('Failed to fetch executions');
    }
  }
  
  // Get system-wide automation statistics
  async getSystemStats(): Promise<SystemAutomationStats> {
    try {
      // Previously read ALL automations + ALL executions with no filter/limit.
      // Now: use getCountFromServer() for counts (1 read each), and limit(500)
      // for the document-level data needed for per-automation stats.
      const automationsRef = collection(db, 'automations');
      const executionsRef  = collection(db, 'automationExecutions');

      const [
        totalAutoSnap,
        activeAutoSnap,
        pausedAutoSnap,
        draftAutoSnap,
        archivedAutoSnap,
        totalExecSnap,
        runningExecSnap,
        completedExecSnap,
        failedExecSnap,
      ] = await Promise.all([
        getCountFromServer(query(automationsRef)),
        getCountFromServer(query(automationsRef, where('status', '==', 'active'))),
        getCountFromServer(query(automationsRef, where('status', '==', 'paused'))),
        getCountFromServer(query(automationsRef, where('status', '==', 'draft'))),
        getCountFromServer(query(automationsRef, where('status', '==', 'archived'))),
        getCountFromServer(query(executionsRef)),
        getCountFromServer(query(executionsRef, where('status', '==', 'running'))),
        getCountFromServer(query(executionsRef, where('status', '==', 'completed'))),
        getCountFromServer(query(executionsRef, where('status', '==', 'failed'))),
      ]);

      const totalAutomations    = totalAutoSnap.data().count;
      const activeAutomations   = activeAutoSnap.data().count;
      const pausedAutomations   = pausedAutoSnap.data().count;
      const draftAutomations    = draftAutoSnap.data().count;
      const archivedAutomations = archivedAutoSnap.data().count;
      const totalExecutions     = totalExecSnap.data().count;
      const runningExecutions   = runningExecSnap.data().count;
      const completedExecutions = completedExecSnap.data().count;
      const failedExecutions    = failedExecSnap.data().count;

      // For per-automation aggregates (emailsSent, successRate, etc.) we need
      // document data but cap at 500 to avoid unbounded reads.
      const [automationsSnap, failedExecsSnap] = await Promise.all([
        getDocs(query(automationsRef, limit(500))),
        getDocs(query(executionsRef, where('status', '==', 'failed'), orderBy('startedAt', 'desc'), limit(50))),
      ]);
      const automations = automationsSnap.docs.map(d => ({ id: d.id, ...d.data() } as Automation));
      const failedExecs = failedExecsSnap.docs.map(d => d.data() as AutomationExecution);
      
      // Calculate total emails/sms/push sent
      const totalEmailsSent = automations.reduce((sum, a) => sum + (a.statistics.emailsSent || 0), 0);
      const totalSmsSent = automations.reduce((sum, a) => sum + (a.statistics.smsSent || 0), 0);
      const totalPushSent = automations.reduce((sum, a) => sum + (a.statistics.pushSent || 0), 0);
      
      // Calculate average completion rate
      const automationsWithRuns = automations.filter(a => a.statistics.totalRuns > 0);
      const averageCompletionRate = automationsWithRuns.length > 0
        ? automationsWithRuns.reduce((sum, a) => {
            const rate = a.statistics.totalRuns > 0 
              ? (a.statistics.successfulRuns / a.statistics.totalRuns) * 100 
              : 0;
            return sum + rate;
          }, 0) / automationsWithRuns.length
        : 0;
      
      // Calculate average execution time
      const automationsWithTime = automations.filter(a => a.statistics.averageCompletionTime);
      const averageExecutionTime = automationsWithTime.length > 0
        ? automationsWithTime.reduce((sum, a) => sum + (a.statistics.averageCompletionTime || 0), 0) / automationsWithTime.length
        : 0;
      
      // Get top performing automations
      const topPerformingAutomations = automations
        .filter(a => a.statistics.totalRuns > 0)
        .map(a => ({
          id: a.id!,
          name: a.name,
          successRate: a.statistics.totalRuns > 0 
            ? (a.statistics.successfulRuns / a.statistics.totalRuns) * 100 
            : 0
        }))
        .sort((a, b) => b.successRate - a.successRate)
        .slice(0, 5);
      
      // Get recent failures from the pre-fetched failed executions
      const recentFailures = failedExecs
        .filter(e => e.error)
        .map(e => ({
          id: e.id,
          error: e.error || 'Unknown error',
          timestamp: e.startedAt ? (e.startedAt as Timestamp).toDate() : new Date()
        }))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 10);
      
      return {
        totalAutomations,
        activeAutomations,
        pausedAutomations,
        draftAutomations,
        archivedAutomations,
        
        totalExecutions,
        runningExecutions,
        completedExecutions,
        failedExecutions,
        
        totalEmailsSent,
        totalSmsSent,
        totalPushSent,
        
        averageCompletionRate,
        averageExecutionTime,
        
        topPerformingAutomations,
        recentFailures
      };
    } catch (error) {
      console.error('Error getting system stats:', error);
      throw new Error('Failed to fetch system statistics');
    }
  }
  
  // Admin action: Force pause automation
  async forceUpdateAutomationStatus(automationId: string, status: 'active' | 'paused' | 'archived'): Promise<void> {
    try {
      const docRef = doc(db, 'automations', automationId);
      await updateDoc(docRef, {
        status,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating automation status:', error);
      throw new Error('Failed to update automation status');
    }
  }
  
  // Admin action: Delete automation
  async forceDeleteAutomation(automationId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Delete automation
      batch.delete(doc(db, 'automations', automationId));
      
      // Delete associated executions
      const executionsQuery = query(
        collection(db, 'automationExecutions'),
        where('automationId', '==', automationId)
      );
      const executionsSnapshot = await getDocs(executionsQuery);
      executionsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting automation:', error);
      throw new Error('Failed to delete automation');
    }
  }
  
  // Admin action: Cancel running execution
  async cancelExecution(executionId: string): Promise<void> {
    try {
      const docRef = doc(db, 'automationExecutions', executionId);
      await updateDoc(docRef, {
        status: 'cancelled',
        completedAt: Timestamp.now(),
        error: 'Cancelled by administrator'
      });
    } catch (error) {
      console.error('Error cancelling execution:', error);
      throw new Error('Failed to cancel execution');
    }
  }
  
  // Get automation by ID with user details
  async getAutomationWithDetails(automationId: string): Promise<AutomationWithUser | null> {
    try {
      const docRef = doc(db, 'automations', automationId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const automation = { id: docSnap.id, ...docSnap.data() } as AutomationWithUser;
      
      // Fetch user details
      try {
        const userDoc = await getDoc(doc(db, 'users', automation.userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          automation.userEmail = userData.email;
          automation.userName = userData.displayName || userData.firstName + ' ' + userData.lastName;
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
      
      return automation;
    } catch (error) {
      console.error('Error getting automation details:', error);
      throw new Error('Failed to fetch automation details');
    }
  }
  
  // Get user's automation count and limits
  async getUserAutomationStats(userId: string): Promise<{
    totalAutomations: number;
    activeAutomations: number;
    totalExecutions: number;
    emailsSent: number;
  }> {
    try {
      const automationsQuery = query(
        collection(db, 'automations'),
        where('userId', '==', userId)
      );
      const automationsSnapshot = await getDocs(automationsQuery);
      const automations = automationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Automation));
      
      const totalAutomations = automations.length;
      const activeAutomations = automations.filter(a => a.status === 'active').length;
      
      const totalExecutions = automations.reduce((sum, a) => sum + (a.statistics.totalRuns || 0), 0);
      const emailsSent = automations.reduce((sum, a) => sum + (a.statistics.emailsSent || 0), 0);
      
      return {
        totalAutomations,
        activeAutomations,
        totalExecutions,
        emailsSent
      };
    } catch (error) {
      console.error('Error getting user automation stats:', error);
      throw new Error('Failed to fetch user automation statistics');
    }
  }
  
  // Bulk operations
  async bulkUpdateStatus(automationIds: string[], status: 'active' | 'paused' | 'archived'): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      automationIds.forEach(id => {
        const docRef = doc(db, 'automations', id);
        batch.update(docRef, {
          status,
          updatedAt: Timestamp.now()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error bulk updating automations:', error);
      throw new Error('Failed to bulk update automations');
    }
  }
  
  async bulkDelete(automationIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      for (const id of automationIds) {
        batch.delete(doc(db, 'automations', id));
        
        // Delete associated executions
        const executionsQuery = query(
          collection(db, 'automationExecutions'),
          where('automationId', '==', id)
        );
        const executionsSnapshot = await getDocs(executionsQuery);
        executionsSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
      }
      
      await batch.commit();
    } catch (error) {
      console.error('Error bulk deleting automations:', error);
      throw new Error('Failed to bulk delete automations');
    }
  }

  // Admin action: Trigger automation manually for a contact
  async triggerAutomationManually(automationId: string, email: string): Promise<string> {
    try {
      // 1. Get the automation to find its userId/workspaceId
      const automation = await this.getAutomationWithDetails(automationId);
      if (!automation) throw new Error('Automation not found');

      // 2. Find or create contact
      // 2. Find or create contact
      const q = query(
        collection(db, 'contacts'),
        where('userId', '==', automation.userId),
        where('email', '==', email.trim().toLowerCase())
      );
      const querySnapshot = await getDocs(q);
      let contact: any = !querySnapshot.empty ? { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() } : null;

      if (!contact) {
        const contactId = await contactService.createContact({
          userId: automation.userId,
          email,
          firstName: 'Manual',
          lastName: 'Trigger',
          source: 'admin_manual_trigger',
          workspaceId: automation.workspaceId || '',
          status: 'subscribed',
          segments: [],
          tags: []
        });
        contact = await contactService.getContact(contactId);
      }

      if (!contact || !contact.id) throw new Error('Failed to identify contact');

      // 3. Trigger via core automation service
      return await automationService.triggerAutomation(automationId, contact.id, {
        triggeredBy: 'admin',
        at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error in triggerAutomationManually:', error);
      throw error;
    }
  }

  // Admin action: Quick edit a step
  async updateAutomationStep(automationId: string, stepId: string, updates: Partial<AutomationStep>): Promise<void> {
    try {
      const automation = await this.getAutomationWithDetails(automationId);
      if (!automation) throw new Error('Automation not found');

      const updatedSteps = automation.steps.map(step => {
        if (step.id === stepId) {
          const config = step.config as any;
          const updatesConfig = updates.config as any;
          const existingInnerConfig = config?.config && typeof config.config === 'object' ? config.config : {};
          const updateInnerConfig = updatesConfig?.config && typeof updatesConfig.config === 'object' ? updatesConfig.config : {};
          const updateRootConfig = updatesConfig && typeof updatesConfig === 'object' ? updatesConfig : {};

          return {
            ...step,
            ...updates,
            config: {
              ...config,
              ...updateRootConfig,
              config: {
                ...existingInnerConfig,
                ...updateInnerConfig
              },
            }
          };
        }
        return step;
      });

      await this.updateAutomation(automationId, {
        steps: updatedSteps
      });
    } catch (error) {
      console.error('Error in updateAutomationStep:', error);
      throw error;
    }
  }

  // Helper to update automation
  private async updateAutomation(automationId: string, updates: Partial<Automation>): Promise<void> {
    const docRef = doc(db, 'automations', automationId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }
}

export const adminAutomationService = new AdminAutomationService();

